import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AuthCard, AuthLayout } from '@/components/features/auth/AuthLayout';
import { AuthForm } from '@/components/features/auth/AuthForm';
import { AuthSuccess } from '@/components/features/auth/status';
import { useAuthRedirect } from '@/hooks/account/useAuthRedirect';
import { AuthMode, AuthState } from '@/lib/schemas/auth';

/**
 * `/auth` - sign in, sign up, request a reset, and the three "check your email"
 * confirmations.
 *
 * Two things left this page in the redesign:
 *
 * - **`BrandBar`.** `AuthLayout` renders `BrandPanel` in its left column, which
 *   already carries the mark and wordmark. Keeping the fixed top bar as well put
 *   the brand on screen twice, most obviously below `md` where the panel stacks
 *   directly beneath the bar.
 * - **`PlaneAnimation`.** Its cleanup was returned from inside an async
 *   function, so `useEffect` received `undefined` and its `repeat: -1` GSAP
 *   timeline leaked on every unmount - of the page users navigate away from by
 *   definition. It also injected two `cdn.jsdelivr.net` script tags per mount,
 *   one of them a paid Club GSAP plugin, had no reduced-motion guard, and was
 *   orange-to-pink against an indigo/lavender token system. It sat unused in
 *   the repo afterwards and has since been deleted.
 */
export function AuthPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<AuthState>('form');
  const [email, setEmail] = useState('');

  useAuthRedirect();

  const mode = (searchParams.get('mode') as AuthMode) || 'login';

  /**
   * `carriedEmail` is the address typed into the form being left behind.
   *
   * It is kept so the swap does not wipe it - `AuthForm` is keyed on `mode` and
   * therefore remounts here, which is what clears a stale form-level error
   * (previously a magic-link error raised in `login` stayed on screen through
   * a switch to `register`) but would also have discarded the email.
   */
  const handleModeChange = (newMode: AuthMode, carriedEmail?: string) => {
    setSearchParams({ mode: newMode });
    setState('form');
    setEmail(carriedEmail ?? '');
  };

  const handleStateChange = (newState: string, userEmail?: string) => {
    // `signed-in` is what register reports when Supabase has email confirmation
    // switched off: signUp returned a live session, so the user is already in
    // and there is no inbox to send them to. `useAuthRedirect` would catch this
    // on the next render anyway; navigating here makes it immediate.
    if (newState === 'signed-in') {
      navigate('/', { replace: true });
      return;
    }
    setState(newState as AuthState);
    if (userEmail) setEmail(userEmail);
  };

  const handleBack = () => {
    setState('form');
    setEmail('');
  };

  useEffect(() => {
    const currentMode = searchParams.get('mode') as AuthMode;
    if (currentMode && ['login', 'register', 'reset'].includes(currentMode)) {
      setState('form');
    } else if (!currentMode) {
      setSearchParams({ mode: 'login' });
    }
  }, [searchParams, setSearchParams]);

  return (
    <AuthLayout>
      <AuthCard>
        {state === 'form' ? (
          <AuthForm
            key={mode}
            mode={mode}
            initialEmail={email}
            onStateChange={handleStateChange}
            onModeChange={handleModeChange}
          />
        ) : (
          <AuthSuccess state={state} email={email} onBack={handleBack} />
        )}
      </AuthCard>
    </AuthLayout>
  );
}
